import * as React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { PropertyImage } from './PropertyImage.js';
import { Avatar } from './avatar.js';
import type { KanbanCase } from '../lib/cases-api.js';
import type { CaseDisplayName } from '../lib/case-title.js';
import {
  CASE_TYPE_LABELS,
  daysInStage,
  isStuckInStage,
  slaStatus,
  SLA_STATUS_BADGE_CLASS_A2,
  SLA_STATUS_LABELS,
  type KanbanCaseType,
} from '../lib/case-stages.js';
import { caseBlockerLine } from '../lib/case-blocker.js';
import { caseProgressView } from '../lib/case-progress.js';
import { V4Progress } from './ux-v4.js';
import type { CaseCostsSummaryItem } from '../lib/costs-api.js';
import { useT } from '../lib/i18n.js';

function formatMoney(amount: string, currency: string): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return `${amount} ${currency}`;
  return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
}

/** Stejna logika jako drivejsi per-kartovy fetch, ted nad polozkou hromadneho souhrnu (krok O6a, polozka 3). */
function deriveCostsLine(
  summary: Pick<CaseCostsSummaryItem, 'totalsByCurrency' | 'commission' | 'marginByCurrency'> | undefined,
): { total: string; currency: string; marginPct: number | null } | null {
  if (summary === undefined) return null;
  const totalWithAmount = summary.totalsByCurrency[0];
  if (totalWithAmount === undefined) return null;
  const margin = summary.marginByCurrency.find((m) => m.currency === totalWithAmount.currency);
  const commission = summary.commission;
  const marginPct =
    margin !== undefined && commission !== null && Number(commission.amount) > 0
      ? Math.round((Number(margin.margin) / Number(commission.amount)) * 100)
      : null;
  return { total: totalWithAmount.total, currency: totalWithAmount.currency, marginPct };
}

/**
 * Krok TAB1 bod 9 — ve kterych fazich ma ukazatel proverení smysl.
 *
 * Poznamka k navrhu u predlohy `ZakazkyKanban.png`: „Ukazatel prověření jen ve
 * fázích Audit a Příprava; u ostatních fází nemá smysl." Zakazka v inzerci uz
 * proverení davno ma — pruh by na ni jen zabiral misto.
 */
const REVIEW_BAR_STAGES: ReadonlySet<string> = new Set(['intake', 'audit', 'preparation']);

export interface KanbanCardProps {
  readonly item: KanbanCase;
  readonly ownerName: string | null;
  /** O8: nazev k zobrazeni misto `item.title` — zastupny text s internim id se netiskne. */
  readonly displayName: CaseDisplayName;
  readonly now: Date;
  readonly onOpen: (id: string) => void;
  /** R5 souhrn nakladu/marze pro tuto zakazku, jiz nactenej hromadne (O6a). undefined = jeste se nenacetl / neni znamy. */
  readonly costsSummary: CaseCostsSummaryItem | undefined;
  /** O6: adresa nahledu titulni fotky nemovitosti; null = zakazka fotku nema. */
  readonly coverUrl: string | null;
  /** Krok DEMO1: souradnice pro mapovy nahled, kdyz `coverUrl` chybi (stejny vzor jako `nemovitosti.tsx`). */
  readonly lat: number | null;
  readonly lon: number | null;
  /** Krok UXF1 bod 2: nemovitost, ke ktere server dopocita obrazek z Mapy.com. */
  readonly propertyId: string | null;
  /**
   * Krok TAB1 bod 9 — druh nemovitosti a obec do radku pod adresou
   * („Pozemek · prodej · Lysá n. L." v predloze). Jde z tehoz davkoveho
   * `fetchPropertyBriefs`, jaky uz stranka vola kvuli adresam (krok O8).
   */
  readonly propertyKindLabel: string | null;
  readonly locality: string | null;
  /** Krok TAB1 bod 9: „co zakazku brzdi" — ukol makleře z `crm_tasks` (viz `lib/case-blocker.ts`). */
  readonly crmNextStep: string | null;
}

/**
 * Karta zakazky podle SCHVALENEHO mockupu — do kroku TAB1 `02-zakazky.html`
 * (UXR1), od TAB1 `docs/design/ux-v3/tabule/ZakazkyKanban.png`:
 *
 *   ortofoto nad nemovitosti · ADRESA tucne · „druh · prodej/pronajem · obec"
 *   · CO ZAKAZKU BRZDI · pruh proverení (jen Audit/Priprava) · „N dni ve fazi"
 *   (cervene nad 30) + avatar maklere.
 *
 * Co predloha nekresli, ale karta mit musi, zustava pod tim jako dalsi radek
 * meta: SLA, pocet upozorneni a naklady/marze (R4, R5, O6a).
 *
 * Presun (drag) je zakazany primo v DOM (`disabled`), kdyz `canUpdate=false` —
 * makler bez prava editace vidi kartu, ale nemuze ji zvednout (zadani R4
 * bod 3, „API 403 -> UI to vi dopredu").
 */
export function KanbanCard({
  item,
  ownerName,
  displayName,
  now,
  onOpen,
  costsSummary,
  coverUrl,
  lat,
  lon,
  propertyId,
  propertyKindLabel,
  locality,
  crmNextStep,
}: KanbanCardProps): React.JSX.Element {
  const t = useT();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    disabled: !item.canUpdate,
    data: { stage: item.stage, allowedTransitions: item.allowedTransitions },
  });

  const style: React.CSSProperties | undefined = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.6 : undefined,
      }
    : undefined;

  const sla = slaStatus(item.slaDueAt, item.slaBreachedAt, item.closedAt, now);
  const days = daysInStage(item.stageEnteredAt, now);
  const stuck = isStuckInStage(item.stageEnteredAt, now);
  const costsLine = deriveCostsLine(costsSummary);
  const dealLabel = CASE_TYPE_LABELS[item.type as KanbanCaseType] ?? item.type;
  const blocker = caseBlockerLine({ nextTaskTitle: item.nextTaskTitle, crmNextStep });
  const showReviewBar = REVIEW_BAR_STAGES.has(item.stage) && item.controlsTotal > 0;
  const daysLabel =
    days === 0 ? t('webAdmin.kanbanCard.daysInStageToday') : t('webAdmin.kanbanCard.daysInStage', { days: String(days) });

  /*
   * Radek pod adresou: „Pozemek · prodej · Lysá n. L." Chybejici cast se
   * VYNECHA, nedoplnuje se pomlckou — makler by si jinak myslel, ze v datech
   * neco je. Makler karty uz je v avataru vpravo dole, do radku se (na rozdil
   * od stavu do TAB1) nepise podruhe.
   */
  const metaParts = [propertyKindLabel, dealLabel, locality].filter(
    (part): part is string => part !== null && part !== '',
  );

  // `title` karty nese oboji, co je k ni potreba dovysvetlit: interni id
  // u zakazky bez nazvu (O8) a duvod, proc s ni nejde hybat (R4).
  const hints = [displayName.hint, item.canUpdate ? undefined : t('webAdmin.kanbanCard.noMovePermission')].filter(
    (hint): hint is string => hint !== undefined,
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="rh-a2-kcard"
      data-testid={`kanban-card-${item.id}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen(item.id);
      }}
      title={hints.length > 0 ? hints.join(' · ') : undefined}
    >
      {/*
        Krok TAB1 bod 9 → krok UXV4a bod 4: obrazek nemovitosti je JEDNA
        komponenta pro cely admin (`components/PropertyImage.tsx`) — fotka,
        letecky vyrez s hranici parcel, panorama, veta „Fotka po nahrání".
        Stred obce v kaskade NENI (nalez R.B. 3. 10., „Vršovická 74 → Staré a
        Nové Město"; predloha UX v4 § Pravidla 3: „Nikdy střed obce").
      */}
      <PropertyImage
        photoUrl={coverUrl}
        propertyId={propertyId}
        lat={lat}
        lon={lon}
        /*
         * Krok UXF1 bod 2: rozmer je ZADOST o pocet pixelu, ne rozmer prvku —
         * server k nemu jeste prida dvojnasobek (retina). Karta je nejsirsi na
         * telefonu (318 px na 390px obrazovce, na desktopu 170 px), takze 360
         * drzi „aspon dvojnasobek pixelu proti plose" na obou sirkach; s
         * puvodnimi 280 vychazel na telefonu podil 1,76 (zmereno 8. 10.).
         */
        size={{ width: 360, height: 180 }}
        className="rh-a2-kcard__cover"
        testIdPrefix="kanban-card"
      />
      <div className="rh-a2-kcard__body">
        <div className="rh-a2-kcard__title">{displayName.text}</div>
        {metaParts.length > 0 ? <div className="rh-a2-kcard__meta">{metaParts.join(' · ')}</div> : null}
        {/*
          Krok UXV4a2 bod 3 — POSTUP NA KAZDE KARTE (predloha § Pravidla 5:
          „Progress bary vždy viditelné"). Procenta i vetu „co chybí" pocita
          TATAZ funkce, jakou pouziva Dnesek a detail zakazky
          (`lib/case-progress.ts`), takze dve obrazovky nemohou rict o teze
          zakazce dve ruzna cisla.
        */}
        <V4Progress view={caseProgressView(item.stage)} testId={`kanban-card-progress-${item.id}`} />
        {blocker !== null ? (
          <div className="rh-a2-kcard__blocker" data-testid={`kanban-card-blocker-${item.id}`}>
            {blocker}
          </div>
        ) : null}
        {showReviewBar ? (
          <ReviewBar done={item.controlsDone} total={item.controlsTotal} caseId={item.id} />
        ) : null}
        <div className="rh-a2-kcard__foot">
          <span
            className={stuck ? 'rh-a2-kcard__days rh-a2-kcard__days--stuck' : 'rh-a2-kcard__days'}
            data-testid={`kanban-card-days-${item.id}`}
            data-stuck={String(stuck)}
          >
            {daysLabel}
          </span>
          {ownerName !== null ? (
            <Avatar name={ownerName} />
          ) : (
            <span className="rh-a2-badge" data-testid="case-unassigned">
              {t('webAdmin.kanban.unassigned')}
            </span>
          )}
        </div>
        {sla !== 'none' || item.attentionCount > 0 || costsLine !== null ? (
          <div className="rh-a2-kcard__tags">
            {sla !== 'none' ? <span className={`rh-a2-badge ${SLA_STATUS_BADGE_CLASS_A2[sla]}`}>{SLA_STATUS_LABELS[sla]}</span> : null}
            {item.attentionCount > 0 ? (
              <span className="rh-a2-badge rh-a2-badge--wait" data-testid="attention-count">
                {item.attentionCount}×
              </span>
            ) : null}
            {costsLine !== null ? (
              <span className="rh-a2-kcard__meta" data-testid="kanban-card-costs">
                {t('webAdmin.kanbanCard.costs')} {formatMoney(costsLine.total, costsLine.currency)}
                {costsLine.marginPct !== null
                  ? ` · ${t('webAdmin.kanbanCard.marginSuffix', { pct: String(costsLine.marginPct) })}`
                  : ''}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Pruh proverení (predloha: segmenty, zaplnene zleva). Je to POCET
 * BLOKUJICICH KONTROL zakazky, ne odhad — `controlsDone/controlsTotal` z
 * `/v1/cases` (viz `CASES_FROM` v `packages/case/src/internal/service.ts`).
 */
function ReviewBar({
  done,
  total,
  caseId,
}: {
  readonly done: number;
  readonly total: number;
  readonly caseId: string;
}): React.JSX.Element {
  const t = useT();
  const label = t('webAdmin.kanbanCard.reviewProgress', { done: String(done), total: String(total) });
  return (
    <div
      className="rh-a2-kcard__bars"
      role="img"
      aria-label={label}
      title={label}
      data-testid={`kanban-card-review-${caseId}`}
      data-done={String(done)}
      data-total={String(total)}
    >
      {Array.from({ length: total }, (_, index) => (
        <span key={index} className={index < done ? 'rh-a2-bar rh-a2-bar--on' : 'rh-a2-bar'} />
      ))}
    </div>
  );
}
